import { Fragment, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Dialog from '../ui/Dialog';
import Field, { FormErrorSummary, FormServerError, rules, useFormValidation, type FormSchema } from '../ui/Field';
import { FormFooter } from '../projects/formBits';
import { api } from '../../services/api';
import { isLegacyPaymentTerm, paymentTermOptions } from '../../utils/paymentTerms';
import { internalRateOf, plusTwelveMonths } from '../../utils/engagementPageModel';
import type { FieldRule } from '../ui/fieldModel';
import type { TypedEngagementTerms } from '../../types';
import PendingDecision from '../ui/PendingDecision';
import ServerTermsWarning from './ServerTermsWarning';
import { activeHourBankOf } from './hourBankConflict';

export type TypedEngagementType = 'service_contract' | 'hour_bank' | 'service_without_contract';
export const TYPED_ENGAGEMENT_TYPES: TypedEngagementType[] = ['service_contract', 'hour_bank', 'service_without_contract'];
export const isTypedEngagementType = (t: string | null | undefined): t is TypedEngagementType =>
  !!t && (TYPED_ENGAGEMENT_TYPES as string[]).includes(t);

type Kind = 'text' | 'number' | 'date' | 'terms' | 'overage' | 'policy';
interface FieldDef { key: string; kind: Kind; required?: boolean; step?: string; positive?: boolean }

/** P-03/B (D-31) — the fields of each type's form; `required` = the * fields. */
const FIELDS: Record<TypedEngagementType, FieldDef[]> = {
  service_contract: [
    { key: 'name', kind: 'text', required: true },
    { key: 'monthly_quota_hours', kind: 'number', required: true, step: '0.5', positive: true },
    { key: 'monthly_price', kind: 'number', required: true, step: '0.01' },
    { key: 'overtime_hourly_rate', kind: 'number', required: true, step: '0.01' },
    { key: 'server_monthly_fee', kind: 'number', step: '0.01' },
    { key: 'server_monthly_cost', kind: 'number', step: '0.01' },
    { key: 'payment_terms', kind: 'terms', required: true },
    { key: 'start_date', kind: 'date', required: true },
    { key: 'end_date', kind: 'date' },
    { key: 'overage_policy', kind: 'overage', required: true },
    { key: 'approved_extra_hours', kind: 'number', step: '0.5' },
  ],
  hour_bank: [
    { key: 'name', kind: 'text', required: true },
    { key: 'purchased_hours', kind: 'number', required: true, step: '0.5', positive: true },
    { key: 'purchase_price', kind: 'number', required: true, step: '0.01' },
    { key: 'internal_hourly_rate', kind: 'number', step: '0.01' },
    { key: 'server_monthly_fee', kind: 'number', step: '0.01' },
    { key: 'server_monthly_cost', kind: 'number', step: '0.01' },
    { key: 'payment_terms', kind: 'terms', required: true },
    { key: 'start_date', kind: 'date', required: true },
    { key: 'expiry_date', kind: 'date', required: true },
  ],
  service_without_contract: [
    { key: 'name', kind: 'text', required: true },
    { key: 'rate', kind: 'number', required: true, step: '0.01' },
    { key: 'server_monthly_fee', kind: 'number', step: '0.01' },
    { key: 'server_monthly_cost', kind: 'number', step: '0.01' },
    { key: 'payment_terms', kind: 'terms', required: true },
    { key: 'start_date', kind: 'date', required: true },
    // EQ-011 — the customer-approval policy (D-262); the threshold shows only for "threshold".
    { key: 'approval_policy', kind: 'policy' },
    { key: 'approval_threshold_hours', kind: 'number', step: '0.5', positive: true },
  ],
};

const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
/** A number ≥ 0 (money, hours that may be 0). */
const nonNegative: FieldRule = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? null : { key: 'engagementForm.nonNegative' };
};
const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v).slice(0, v instanceof Date ? 10 : undefined));

/**
 * P-03/B + D6 — the per-type form of a service contract, an hour bank or a
 * לל"ח: create (the customer is locked; parent + child with every term in one
 * request, status active) or edit (name, dates and terms; audited server-side).
 * An hour bank's purchased hours and expiry keep the existing purchase rules
 * in edit: filled in while empty, otherwise read-only (new purchase /
 * adjustment / "הארכת תוקף").
 */
export default function TypedEngagementForm({
  mode, type, organizationId, organizationName, engagementId, initial, initialName, characterizationId, onClose, onSaved,
}: {
  mode: 'create' | 'edit';
  type: TypedEngagementType;
  organizationId: string;
  organizationName?: string;
  /** edit: the engagement being edited. */
  engagementId?: string;
  /** edit: the child row (API field names). */
  initial?: Record<string, unknown> | null;
  /** create: a name already typed elsewhere (the general form). */
  initialName?: string;
  /** P-07 (D-74 §8) — create: opened from an "אפיון" card; the server links it in the same transaction. */
  characterizationId?: string;
  onClose: () => void;
  onSaved: (engagementId: string) => void;
}) {
  const { t } = useTranslation();
  const defs = FIELDS[type];

  const { data: orgRes } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => api.getOrganization(organizationId),
    enabled: mode === 'create' && !organizationName && !!organizationId,
  });
  const customerName = organizationName ?? orgRes?.data?.name ?? '';

  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    for (const d of defs) v[d.key] = mode === 'edit' ? str(initial?.[d.key]).slice(0, d.kind === 'date' ? 10 : undefined) : '';
    if (mode === 'create') {
      v.name = initialName ?? '';
      if (type === 'service_contract') { v.overage_policy = 'approval'; v.approved_extra_hours = '0'; }
      if (type === 'service_without_contract') v.approval_policy = 'none'; // = the DB default
      if (type === 'hour_bank' || type === 'service_without_contract') v.start_date = todayIso();
      if (type === 'hour_bank') v.expiry_date = plusTwelveMonths(v.start_date);
    }
    return v;
  });
  // B2 — the internal rate / expiry follow price ÷ hours / start + 12 months until edited by hand.
  const [touched, setTouched] = useState<Record<string, boolean>>({ internal_hourly_rate: mode === 'edit', expiry_date: mode === 'edit' });

  // FQ-021 (D-223 §1): the price of a purchase is fixed once an hour of it was used
  // (the server answers 409). The edited purchase is the engagement's first bank (`initial`).
  const { data: bankSnap } = useQuery({
    queryKey: ['hour-bank', engagementId],
    queryFn: () => api.getHourBank(engagementId!).then((r) => r.data),
    enabled: mode === 'edit' && type === 'hour_bank' && !!engagementId,
  });
  const editedBank = bankSnap?.banks?.find((b) => b.id === initial?.id);
  // FIX-B27 (QX-452): a purchase that never had a price may get its first one even with usage.
  const priceLocked = mode === 'edit' && type === 'hour_bank' && Number(editedBank?.used_hours ?? 0) > 0
    && str(initial?.purchase_price) !== '';

  // Edit: an hour bank purchase's hours / expiry are fixed once set (rule 7).
  const locked = (key: string): boolean =>
    (key === 'purchase_price' && priceLocked) ||
    (mode === 'edit' && type === 'hour_bank' && (key === 'purchased_hours' || key === 'expiry_date') && str(initial?.[key]) !== '');

  const set = (key: string, value: string) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      if (type === 'hour_bank') {
        if ((key === 'purchase_price' || key === 'purchased_hours') && !touched.internal_hourly_rate) {
          next.internal_hourly_rate = internalRateOf(next.purchase_price, next.purchased_hours);
        }
        if (key === 'start_date' && !touched.expiry_date && !locked('expiry_date')) {
          next.expiry_date = plusTwelveMonths(value);
        }
      }
      return next;
    });
    if (key === 'internal_hourly_rate' || key === 'expiry_date') setTouched((p) => ({ ...p, [key]: true }));
  };

  const schema: FormSchema = {};
  for (const d of defs) {
    const r = [];
    if (d.required) r.push(rules.required());
    if (d.key === 'approval_threshold_hours') {
      // only when the policy is "threshold": a positive number of hours is required
      if (values.approval_policy === 'threshold') schema[`tef-${d.key}`] = [rules.required(), rules.numberAbove(0)];
      continue;
    }
    if (d.kind === 'number') r.push(d.positive ? rules.numberAbove(0) : nonNegative);
    if (r.length) schema[`tef-${d.key}`] = r;
  }
  if (type === 'service_contract') schema['tef-end_date'] = [rules.after('tef-start_date')];
  if (type === 'hour_bank') schema['tef-expiry_date'] = [...(schema['tef-expiry_date'] ?? []), rules.after('tef-start_date')];
  const formValues = Object.fromEntries(defs.map((d) => [`tef-${d.key}`, values[d.key]]));
  const v = useFormValidation(schema, formValues);
  const labels = Object.fromEntries(defs.map((d) => [`tef-${d.key}`, t(`engagementForm.fields.${d.key}`)]));

  const toBody = (): Record<string, unknown> => {
    const body: Record<string, unknown> = {};
    for (const d of defs) {
      if (locked(d.key)) continue;
      if (d.key === 'approval_threshold_hours' && values.approval_policy !== 'threshold') continue;
      const raw = values[d.key]?.trim() ?? '';
      const val = raw === '' ? null : d.kind === 'number' ? Number(raw.replace(',', '.')) : raw;
      if (mode === 'edit') {
        const before = str(initial?.[d.key]).slice(0, d.kind === 'date' ? 10 : undefined);
        const same = d.kind === 'number' ? (before === '' ? val === null : Number(before) === val) : (before === '' ? val === null : before === val);
        if (same) continue;
      } else if (val === null) {
        continue;
      }
      body[d.key] = val;
    }
    return body;
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const body = toBody();
      if (mode === 'create') {
        const r = await api.createEngagement({
          type, organization_id: organizationId, name: String(body.name ?? ''), ...(body as TypedEngagementTerms),
          ...(characterizationId ? { characterization_id: characterizationId } : {}),
        });
        return r.data?.id ?? '';
      }
      if (Object.keys(body).length > 0) await api.updateEngagement(engagementId!, body as TypedEngagementTerms & { name?: string });
      return engagementId!;
    },
    onSuccess: (id) => onSaved(id),
  });

  const submit = v.submit(() => mutation.mutate());
  const typeLabel = t(`engagementDetail.types.${type}`);
  const title = mode === 'create'
    ? t('engagementForm.titleNew', { type: typeLabel, customer: customerName })
    : t('engagementForm.titleEdit', { type: typeLabel, name: str(initial?.name) });

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      size="lg"
      dismissible={!mutation.isPending}
      footer={<FormFooter formId="typed-eng-form" busy={mutation.isPending} submitLabel={mode === 'create' ? t('engagements.createAction') : t('common.save')} onCancel={onClose} />}
    >
      <form id="typed-eng-form" onSubmit={submit} noValidate className="space-y-4">
        {v.showSummary && <FormErrorSummary errors={v.errors} labels={labels} order={Object.keys(schema)} />}
        <FormServerError error={mutation.error} fallback={mode === 'create' ? t('engagements.errors.create') : t('engagementForm.saveError')} />
        {/* EQ-004 — the customer already has an active bank: point to "רכישה חדשה" in it. */}
        {(() => {
          const ex = activeHourBankOf(mutation.error);
          return ex ? (<p className="text-sm"><Link to={`/engagements/${ex.engagementId}`} className="font-medium text-primary-700 hover:underline">{t('engagementForm.hourBankExists.link')}</Link></p>) : null;
        })()}
        <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2">
          {defs.map((d) => {
            const id = `tef-${d.key}`;
            if (d.key === 'approval_threshold_hours' && values.approval_policy !== 'threshold') return null;
            const hint = locked(d.key)
              ? t(d.key === 'expiry_date' ? 'engagementForm.hints.expiryLocked'
                : d.key === 'purchase_price' ? 'engagementForm.hints.priceLocked' : 'engagementForm.hints.hoursLocked')
              : d.key === 'internal_hourly_rate' ? t('engagementForm.hints.internalRate')
              : undefined;
            const field = (
              <Field key={d.key} id={id} label={labels[id]} required={d.required} error={v.errors[id]} hint={hint}
                className={d.key === 'name' ? 'sm:col-span-2' : undefined}>
                {d.kind === 'terms' ? (
                  <select value={values[d.key]} onChange={(e) => set(d.key, e.target.value)} onBlur={() => v.blur(id)} className="input">
                    <option value="">{t('engagementForm.pickTerms')}</option>
                    {paymentTermOptions(values[d.key] || null).map((p) => <option key={p} value={p} disabled={isLegacyPaymentTerm(p)}>{t(`paymentTerms.${p}`)}</option>)}
                  </select>
                ) : d.kind === 'policy' ? (
                  <select value={values[d.key]} onChange={(e) => set(d.key, e.target.value)} onBlur={() => v.blur(id)} className="input">
                    {['none', 'per_task', 'threshold'].map((p) => <option key={p} value={p}>{t(`rates.policy.${p}`)}</option>)}
                  </select>
                ) : d.kind === 'overage' ? (
                  <select value={values[d.key]} onChange={(e) => set(d.key, e.target.value)} onBlur={() => v.blur(id)} className="input">
                    <option value="approval">{t('engagementForm.overage.approval')}</option>
                    <option value="charge">{t('engagementForm.overage.charge')}</option>
                  </select>
                ) : (
                  <input
                    type={d.kind === 'number' ? 'number' : d.kind === 'date' ? 'date' : 'text'}
                    inputMode={d.kind === 'number' ? 'decimal' : undefined}
                    step={d.step}
                    min={d.kind === 'number' ? 0 : undefined}
                    value={values[d.key]}
                    disabled={locked(d.key)}
                    onChange={(e) => set(d.key, e.target.value)}
                    onBlur={() => v.blur(id)}
                    className="input"
                    autoComplete="off"
                  />
                )}
              </Field>
            );
            // D-125 PM-11 / PM-12: the server charge (partial month; in arrears or in advance)
            if (d.key === 'server_monthly_fee') {
              return (
                <div key={d.key} className="relative">
                  <span className="absolute end-0 top-0 inline-flex gap-1">
                    <PendingDecision id="PM-11" />
                    <PendingDecision id="PM-12" />
                  </span>
                  {field}
                </div>
              );
            }
            // P-34 (D-188): a warning (never a block) when the server has a cost and no price, or costs more than its price.
            if (d.key === 'server_monthly_cost') {
              return (
                <Fragment key={d.key}>
                  {field}
                  <ServerTermsWarning fee={values.server_monthly_fee} cost={values.server_monthly_cost} />
                </Fragment>
              );
            }
            return field;
          })}
        </div>
      </form>
    </Dialog>
  );
}
